import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";

import { CampaignStepOrders, CampaignStepTitles } from "../schema";

interface Props {
  activeStep: number;
  handleClose: () => void;
  campaignName?: string;
  onRename?: (newName: string) => Promise<void>;
}

const StepperHeader = ({
  activeStep,
  handleClose,
  campaignName,
  onRename,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(campaignName ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraftName(campaignName ?? "");
  }, [campaignName, isEditing]);

  const trimmedDraft = draftName.trim();
  const canSave =
    !saving && trimmedDraft.length > 0 && trimmedDraft !== campaignName;

  const handleStartEdit = () => {
    setDraftName(campaignName ?? "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setDraftName(campaignName ?? "");
  };

  const handleSave = async () => {
    if (!onRename || !canSave) return;
    try {
      setSaving(true);
      await onRename(trimmedDraft);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };
  if (isMobileScreen) {
    return (
      <Grid
        container
        justifyContent="center"
        sx={{ mt: 3 }}
        flexDirection={"column"}
        alignItems="center"
      >
        <Box
          sx={{
            py: 1,
            px: 2,
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 8,
            textAlign: "center",
            fontSize: 12,
          }}
        >
          Step {activeStep + 1}/{CampaignStepOrders.length}
        </Box>

        <Typography variant="h5" textAlign={"center"} sx={{ mt: 2, mb: 0 }}>
          {CampaignStepTitles[CampaignStepOrders[activeStep]]}
        </Typography>
      </Grid>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 2.5, md: 4 },
        pt: { xs: 3, md: 4 },
        pb: 3,
        position: "relative",
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          width: 25,
          height: 25,
        }}
      >
        <Box
          component="i"
          className="fa-regular fa-xmark"
          sx={{ fontSize: 20 }}
        />
      </IconButton>

      <Box sx={{ mb: 3 }}>
        {isEditing && campaignName && onRename ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 700,
                lineHeight: 1.3,
                whiteSpace: "nowrap",
              }}
            >
              Campaign:
            </Typography>
            <TextField
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSave) handleSave();
                if (e.key === "Escape") handleCancel();
              }}
              size="small"
              autoFocus
              disabled={saving}
              inputProps={{ maxLength: 200 }}
              sx={{
                flex: 1,
                maxWidth: 420,
                "& .MuiInputBase-input": {
                  fontSize: { xs: 18, md: 22 },
                  fontWeight: 700,
                  py: 0.5,
                },
              }}
            />
            <IconButton
              onClick={handleSave}
              disabled={!canSave}
              size="small"
              sx={{ color: "primary.main" }}
              aria-label="Save campaign name"
            >
              {saving ? (
                <CircularProgress size={16} />
              ) : (
                <Box
                  component="i"
                  className="fa-regular fa-check"
                  sx={{ fontSize: 16 }}
                />
              )}
            </IconButton>
            <IconButton
              onClick={handleCancel}
              disabled={saving}
              size="small"
              sx={{ color: "text.secondary" }}
              aria-label="Cancel rename"
            >
              <Box
                component="i"
                className="fa-regular fa-xmark"
                sx={{ fontSize: 16 }}
              />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 700,
                lineHeight: 1.3,
              }}
            >
              {campaignName
                ? `Campaign: ${campaignName}`
                : "Create New Campaign"}
            </Typography>
            {campaignName && onRename && (
              <Tooltip title="Rename campaign">
                <IconButton
                  onClick={handleStartEdit}
                  size="small"
                  sx={{ color: "text.secondary" }}
                  aria-label="Rename campaign"
                >
                  <Box
                    component="i"
                    className="fa-regular fa-pen"
                    sx={{ fontSize: 14 }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        <Typography sx={{ fontSize: 14, color: "#4A5565", mt: 0.5 }}>
          Follow these simple steps to launch your campaign
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: { xs: "100%", md: 520 },
        }}
      >
        {CampaignStepOrders.map((label, index) => {
          const isCompleted = index < activeStep;
          const isActive = index === activeStep;
          const isLast = index === CampaignStepOrders.length - 1;

          return (
            <Box
              key={label}
              sx={{
                display: "flex",
                alignItems: "center",
                flex: isLast ? 0 : 1,
              }}
            >
              {/* Circle + label */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 35,
                    height: 35,
                    borderRadius: "50%",
                    bgcolor:
                      isCompleted || isActive ? "primary.main" : "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {isCompleted ? (
                    <Box
                      component="i"
                      className="fa-solid fa-check"
                      sx={{ fontSize: 12, color: "white" }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: isActive ? "white" : "grey.500",
                      }}
                    >
                      {index + 1}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "primary.main" : "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {CampaignStepTitles[label]}
                </Typography>
              </Box>
              {/* Divider line between circles */}
              {!isLast && (
                <Box
                  sx={{
                    height: "1px",
                    bgcolor: isCompleted ? "primary.main" : "grey.200",
                    mx: 1,
                    width: 50,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StepperHeader;
